import { useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { listAdminUsers } from '../../../api/admin'
import { Button, ButtonLink } from '../../../components/ui/Button'
import { Card, ErrorBox } from '../../../components/ui/Card'
import { useFetch } from '../../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass, skeletonClass } from '@/lib/tableStyles'
import type { AdminUser } from '../../../types/admin'
import type { UserRole } from '../../../types/auth'

const PAGE_SIZE = 25

const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  org_admin: 'Org Admin',
  staff: 'Staff',
  client: 'Client',
  user: 'User',
}

const ROLE_COLORS: Record<UserRole, { bg: string; color: string }> = {
  super_admin: { bg: 'var(--accent-muted)', color: 'var(--accent)' },
  org_admin: { bg: 'var(--surface-alt)', color: 'var(--text-2)' },
  staff: { bg: 'var(--surface-alt)', color: 'var(--text-2)' },
  client: { bg: 'var(--surface-alt)', color: 'var(--text-3)' },
  user: { bg: 'var(--surface-alt)', color: 'var(--text-3)' },
}

function Badge({ role }: { role: UserRole }) {
  return (
    <span
      className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-xs font-semibold"
      style={{ background: ROLE_COLORS[role].bg, color: ROLE_COLORS[role].color }}
    >
      {ROLE_LABELS[role]}
    </span>
  )
}

function StatusDot({ active }: { active: boolean }) {
  return (
    <span className={`inline-flex items-center gap-[0.35rem] text-[0.82rem] ${active ? 'text-success-text' : 'text-danger-text'}`}>
      <span className={`w-[7px] h-[7px] rounded-full inline-block ${active ? 'bg-success-text' : 'bg-danger-text'}`} />
      {active ? 'Active' : 'Inactive'}
    </span>
  )
}

export default function UserList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q') ?? ''
  const roleFilter = (searchParams.get('role') ?? '') as UserRole | ''
  const activeFilter = searchParams.get('active') ?? ''
  const offset = Number(searchParams.get('offset') ?? '0')

  const [inputValue, setInputValue] = useState(search)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isActiveParam = activeFilter === 'true' ? true : activeFilter === 'false' ? false : undefined

  const { data, loading, error } = useFetch(
    () => listAdminUsers({
      search: search || undefined,
      role: roleFilter || undefined,
      is_active: isActiveParam,
      limit: PAGE_SIZE,
      offset,
    }),
    [search, roleFilter, activeFilter, offset],
  )

  const users: AdminUser[] = data?.items ?? []
  const total = data?.total ?? 0

  const updateParams = (updates: Record<string, string>) => {
    const next = new URLSearchParams(searchParams)
    for (const [k, v] of Object.entries(updates)) {
      if (v === '') next.delete(k)
      else next.set(k, v)
    }
    next.delete('offset')
    setSearchParams(next, { replace: true })
  }

  const handleSearch = (value: string) => {
    setInputValue(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      updateParams({ q: value })
    }, 300)
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1
  const start = total === 0 ? 0 : offset + 1
  const end = Math.min(offset + PAGE_SIZE, total)

  const prevOffset = Math.max(0, offset - PAGE_SIZE)
  const nextOffset = offset + PAGE_SIZE

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <h1 className="m-0 text-[1.4rem] font-bold text-text-heading">Users</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          className="py-[0.55rem] px-3 border border-border-input rounded text-sm outline-none bg-surface text-text w-[280px]"
          type="search"
          placeholder="Search name or email…"
          value={inputValue}
          onChange={(e) => handleSearch(e.target.value)}
        />

        <select
          className="py-[0.55rem] px-3 border border-border-input rounded text-sm outline-none bg-surface text-text cursor-pointer"
          value={roleFilter}
          onChange={(e) => updateParams({ role: e.target.value })}
        >
          <option value="">All roles</option>
          <option value="super_admin">Super Admin</option>
          <option value="org_admin">Org Admin</option>
          <option value="staff">Staff</option>
          <option value="client">Client</option>
        </select>

        <select
          className="py-[0.55rem] px-3 border border-border-input rounded text-sm outline-none bg-surface text-text cursor-pointer"
          value={activeFilter}
          onChange={(e) => updateParams({ active: e.target.value })}
        >
          <option value="">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className={thClass}>Name / Email</th>
                <th className={thClass}>Role</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Verified</th>
                <th className={thClass}>Joined</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className={tdClass}>
                        <div className={`${skeletonClass} h-3.5 ${j === 6 ? 'w-12' : 'w-3/4'}`} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className={`${tdClass} text-center py-12 text-text-3`}>
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td className={tdClass}>
                      <div className="font-semibold text-text">{u.full_name ?? '—'}</div>
                      <div className="text-[0.8rem] text-text-2">{u.email}</div>
                    </td>
                    <td className={tdClass}><Badge role={u.role} /></td>
                    <td className={tdClass}><StatusDot active={u.is_active} /></td>
                    <td className={`${tdClass} text-[0.82rem] ${u.email_verified ? 'text-success-text' : 'text-danger-text'}`}>
                      {u.email_verified ? 'Yes' : 'No'}
                    </td>
                    <td className={`${tdClass} text-text-2 text-[0.82rem]`}>
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <ButtonLink to={`/admin/users/${u.id}`} variant="outline" size="sm">
                        Manage
                      </ButtonLink>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && total > 0 && (
          <div className="flex items-center justify-between px-4 py-[0.85rem] border-t border-border text-[0.85rem] text-text-2">
            <span>Showing {start}–{end} of {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => { const p = new URLSearchParams(searchParams); p.set('offset', String(prevOffset)); setSearchParams(p, { replace: true }) }}>
                ← Prev
              </Button>
              <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => { const p = new URLSearchParams(searchParams); p.set('offset', String(nextOffset)); setSearchParams(p, { replace: true }) }}>
                Next →
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
